import React from 'react';
import { Cell } from 'react-table';
import _round from 'lodash/round';
import _startCase from 'lodash/startCase';

import { useModal } from '../../../../../../../hooks';
import { InviteCollaborators } from '../../../../../../modals';
import { KnownShare } from './KnownShare';

/**
 * @typedef {object} PercentSharesCellProps
 * @property {object} cell the react-table Cell instance for a TrqkSong
 * @property {column} column a PercentSharesColumn instance for a TrqkSong
 * @property {row} row the react-table Row instance for a TrqkSong
 */
interface PercentSharesCellProps {
    cell: Cell<TrqkSong>;
}

/**
 * Displays details about the ownership for a track, as defined by the percent_share roleType, i.e.
 * 'writer' or 'publisher'. This cell is used in both the Writers column and the Publishers column.
 * It is assumed that the Writers own 50% of the total shares and the Publishers own 50% of the
 * total shares. The percent_share is treated as a portion of this subset of the total shares, thus
 * `roleType: "writer"` and `percent_share: 100` is taken to mean "all of the writer's share, or 50%
 * of the total ownership".
 *
 * If there are any unknown shares for this subset, an "Invite" link is also shown.
 * @param {PercentSharesCellProps}
 */
export const PercentSharesCell = ({
    cell,
}: PercentSharesCellProps): JSX.Element | string => {
    const name = cell?.value?.name ?? null;
    const roleType = cell?.value?.roleType ?? null;
    const totalShares = cell?.value?.totalShares ?? 50;
    const percentShare = cell?.value?.percent_share ?? null;
    const title = cell?.value?.title ?? null;
    const trqkSongId = cell?.value?.trqkSongId ?? null;
    const modal = useModal();

    if (trqkSongId === null) {
        // unicode em dash
        return <>&#8212;</>;
    }

    // See notes about math above. We are assuming the `title` is applied to either the
    // Writers share (50%) or the Publishers share (50%) of the entire ownership.
    const unknownShares =
        percentShare === null
            ? totalShares
            : totalShares - (totalShares / 100) * percentShare;
    const knownShare = totalShares - unknownShares;

    const InviteCollaboratorsModal = ({
        remove,
    }: ModalContentProps<Record<string, never>>): JSX.Element => (
        <InviteCollaborators
            remove={remove}
            title={title}
            trqkSongId={trqkSongId}
        />
    );

    const unknownRoleLabel =
        knownShare > 0 ? `Co-${roleType}` : _startCase(roleType);

    return (
        <div className="pr-3 w-full text-xs">
            {knownShare > 0 && <KnownShare share={knownShare} name={name} />}
            {unknownShares > 0 && (
                <div>
                    <span className="italic mr-1">
                        {`${unknownRoleLabel}s: ${_round(unknownShares, 0)}%`}
                    </span>
                    <button
                        className="text-primary-500 underline"
                        type="button"
                        onClick={(): void => {
                            modal.add({
                                Content: InviteCollaboratorsModal,
                            });
                        }}
                    >
                        Invite
                    </button>
                </div>
            )}
        </div>
    );
};
